import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Platform,
  ScrollView,
  ActivityIndicator,
} from 'react-native';
import { ScreenLayout, Header, FormHeader } from '../components';
import { QuickReplyItemForm, QuickReplyItemFormData } from '../components/quick-replies';
import { apiService } from '../services/api';
import { useAppContext } from '../contexts/AppContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { colors, spacing, typography } from '../theme';
import { useThemedStyles } from '../hooks/useThemedStyles';

interface QuickReplyItemEditScreenProps {
  itemId?: string;
}

export const QuickReplyItemEditScreen: React.FC<QuickReplyItemEditScreenProps> = ({ itemId }) => {
  const styles = useThemedStyles(getStyles);
  const { navigateTo, showNotification, activeTab, setActiveTab } = useAppContext();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [item, setItem] = useState<any>(null);
  const [availableRepliesSets, setAvailableRepliesSets] = useState<Array<{ _id: string; name: string }>>([]);
  const formSubmitRef = useRef<(() => void) | null>(null);

  // Get itemId from URL params if not provided as prop (for web)
  const actualItemId = itemId || (Platform.OS === 'web' ? new URLSearchParams(window.location.search).get('itemId') : null);

  useEffect(() => {
    if (actualItemId) {
      loadItem();
      loadAvailableSets();
    } else {
      showNotification('error', 'Error', 'Item ID is required');
      navigateTo('quick-reply-items');
    }
  }, [actualItemId]);

  const loadItem = async () => {
    if (!actualItemId) return;

    try {
      setLoading(true);
      const response = await apiService.getQuickReplyItemById(actualItemId);

      if (response.statusCode === 1000 && response.data) {
        setItem(response.data);
      } else {
        const errorMessage = response.errors?.[0]?.message || 'Failed to load item';
        showNotification('error', 'Error', errorMessage);
        navigateTo('quick-reply-items');
      }
    } catch (error: any) {
      console.error('Error loading item:', error);
      showNotification('error', 'Error', error.message || 'Failed to load item');
      navigateTo('quick-reply-items');
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableSets = async () => {
    try {
      const response = await apiService.getQuickReplySets({
        limit: 1000,
        skip: 0,
      });

      if (response.statusCode === 1000 && response.data?.quickReplySets) {
        setAvailableRepliesSets(
          response.data.quickReplySets.map((set: any) => ({
            _id: set._id,
            name: set.name,
          }))
        );
      }
    } catch (error: any) {
      console.error('Error loading sets:', error);
    }
  };

  /** 'flow' type items trigger their flow via the existing reply_item-scoped FlowAttachment
   * mechanism, not the item's own config — so editing an item needs to keep that attachment in
   * sync with whatever the form just saved: delete the old attachment if the type moved away
   * from 'flow' or the selected flow changed, create a fresh one if it's now 'flow' and none
   * (matching) exists. Always delete-then-recreate on a real change rather than trying to
   * PUT a new flowId in place — updateFlowAttachment doesn't support repointing flowId, and
   * delete+create is simple and correct regardless. */
  const reconcileFlowAttachment = async (itemData: QuickReplyItemFormData) => {
    if (!actualItemId) return;
    try {
      const existingResult = await apiService.getFlowAttachments({ scope: 'reply_item', attachedTo: actualItemId });
      const existing: any[] = Array.isArray(existingResult.data) ? existingResult.data : [];

      const wantsFlow = itemData.type === 'flow' && !!itemData.config?.flowId;
      const alreadyCorrect = wantsFlow && existing.some((a) => a.flowId === itemData.config.flowId && a.isActive !== false);

      if (!alreadyCorrect) {
        for (const attachment of existing) {
          await apiService.deleteFlowAttachment(attachment._id);
        }
        if (wantsFlow) {
          await apiService.createFlowAttachment({
            flowId: itemData.config.flowId,
            scope: 'reply_item',
            attachedTo: actualItemId,
          });
        }
      }
    } catch (error: any) {
      console.error('Error reconciling flow attachment:', error);
      showNotification('error', 'Warning', 'Item was saved, but syncing its flow attachment failed. Edit the item to try again.');
    }
  };

  const handleSubmit = async (itemData: QuickReplyItemFormData) => {
    if (!actualItemId) return;

    try {
      setSaving(true);
      const response = await apiService.updateQuickReplyItemStandalone(actualItemId, {
        displayText: itemData.displayText,
        type: itemData.type,
        config: itemData.config || {},
        displayOrder: itemData.displayOrder || 0,
        isActive: itemData.isActive !== undefined ? itemData.isActive : true,
        icon: itemData.icon,
        bodyText: itemData.bodyText,
        setIds: itemData.setIds || [],
      });

      if (response.statusCode === 1000) {
        await reconcileFlowAttachment(itemData);
        showNotification('success', 'Success', 'Item updated successfully.');
        if (Platform.OS === 'web') {
          window.history.back();
        } else {
          navigateTo('quick-reply-items');
        }
      } else {
        const errorMessage = response.errors?.[0]?.message || 'Failed to update item';
        showNotification('error', 'Error', errorMessage);
      }
    } catch (error: any) {
      console.error('Error updating item:', error);
      showNotification('error', 'Error', error.message || 'Failed to update item');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (Platform.OS === 'web') {
      window.history.back();
    } else {
      navigateTo('quick-reply-items');
    }
  };

  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);

  if (loading) {
    return (
      <ScreenLayout
        header={
          <Header
            showNotifications={true}
            showMessages={true}
            onNotificationPress={() => navigateTo('notifications')}
            onMessagePress={() => navigateTo('chat')}
            showNavigation={Platform.OS === 'web'}
            activeTab={activeTab}
            onTabPress={handleTabPress}
          />
        }
        showBottomTabs={Platform.OS !== 'web'}
        onLeftPaneClose={() => {}}
        leftPaneContent="settings"
        onSettingsItemPress={async (itemId) => {
          const { setPendingSettingsItem } = await import('../utils/tempStorage');
          await setPendingSettingsItem(itemId);
          navigateTo('settings');
        }}
        onRightPaneClose={() => {}}
        rightPaneContent="empty"
      >
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text style={styles.loadingText}>Loading item...</Text>
        </View>
      </ScreenLayout>
    );
  }

  if (!item) {
    return null;
  }

  return (
    <ScreenLayout
      header={
        <Header
          showNotifications={true}
          showMessages={true}
          onNotificationPress={() => navigateTo('notifications')}
          onMessagePress={() => navigateTo('chat')}
          showNavigation={Platform.OS === 'web'}
          activeTab={activeTab}
          onTabPress={handleTabPress}
        />
      }
      showBottomTabs={Platform.OS !== 'web'}
      onLeftPaneClose={() => {}}
      leftPaneContent="settings"
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      onRightPaneClose={() => {}}
      rightPaneContent="empty"
      formHeader={
        <FormHeader
          title="Edit Item"
          subtitle="Update quick reply item details"
          onBack={handleCancel}
          actions={[
            {
              icon: 'git-branch-outline',
              onPress: () =>
                navigateTo('dispatch-rules-list', undefined, {
                  scope: 'reply_item',
                  attachedTo: actualItemId,
                  attachedToLabel: item?.displayText,
                }),
              accessibilityLabel: 'Rules',
            },
            {
              icon: 'checkmark',
              onPress: () => formSubmitRef.current?.(),
              primary: true,
              loading: saving,
              disabled: saving,
            },
          ]}
        />
      }
    >
      <View style={styles.wrapper}>
        <ScrollView
          style={styles.container}
          contentContainerStyle={styles.contentContainer}
          showsVerticalScrollIndicator={false}
        >
          <View style={styles.formContainer}>
            <QuickReplyItemForm
              initialData={{
                displayText: item.displayText,
                type: item.type,
                config: item.config,
                displayOrder: item.displayOrder,
                isActive: item.isActive,
                icon: item.icon,
                bodyText: item.bodyText,
                setIds: item.setIds || [],
              }}
              onSubmit={handleSubmit}
              submitLabel="Save Changes"
              loading={saving}
              availableRepliesSets={availableRepliesSets}
              hideActions={true}
              submitRef={formSubmitRef}
            />
          </View>
        </ScrollView>
      </View>
    </ScreenLayout>
  );
};

const getStyles = () => StyleSheet.create({
  wrapper: {
    flex: 1,
  },
  container: {
    flex: 1,
    backgroundColor: colors.background.base,
  },
  contentContainer: {
    paddingBottom: spacing.xl,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: spacing.md,
    fontSize: typography.fontSize.base,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
  },
  formContainer: {
    padding: spacing.md,
  },
});

